<template>
    <b-modal
        ref="modal"
        centered
        hide-footer
        size="lg"
        :title="modalTitle"
        @hidden="handleHidden"
    >
        <b-alert
            v-if="errorMessage"
            variant="danger"
            show
        >
            <div class="alert-body">
                {{ errorMessage }}
            </div>
        </b-alert>

        <b-form @submit.prevent="submit">
            <b-form-group
                v-if="isAddMode"
                :label="translate(
                    'Page(ReceivedRequest):Component(ProductStructure):component-name',
                    'Component name'
                )"
            >
                <vue-autosuggest
                    :suggestions="suggestions"
                    :input-props="inputProps"
                    :get-suggestion-value="getSuggestionValue"
                    @input="search"
                    @selected="selectSuggestion"
                >
                    <template #default="{ suggestion }">
                        <div class="d-flex justify-content-between">
                            <span>{{ suggestion.item.title }}</span>
                            <small v-if="suggestion.item.productId" class="text-less-muted">
                                {{ suggestion.item.productId }}
                            </small>
                        </div>
                    </template>
                </vue-autosuggest>
                <small v-if="selectedProduct" class="text-success">
                    {{ translate(
                        'Page(ReceivedRequest):Component(ProductStructure):existing-product-selected',
                        'Existing product selected'
                    ) }}
                </small>
            </b-form-group>

            <b-form-group
                v-else
                :label="translate(
                    'Page(ReceivedRequest):Component(ProductStructure):component',
                    'Component'
                )"
            >
                <b-form-input :value="name" disabled />
            </b-form-group>

            <b-form-group
                v-if="isNewProduct"
                :label="translate(
                    'Component(ProductComponentsDefinition):select-product-type',
                    'Select product type'
                )"
            >
                <b-form-radio-group v-model="productType">
                    <b-form-radio :value="productTypes.own">
                        {{ translate('Component(Text):product-type-own', 'Own product') }}
                    </b-form-radio>
                    <b-form-radio :value="productTypes.purchased">
                        {{ translate('Component(Text):product-type-purchased', 'Purchased product') }}
                    </b-form-radio>
                </b-form-radio-group>
            </b-form-group>

            <b-row>
                <b-col md="6">
                    <b-form-group
                        :label="translate(
                            'Component(SelectOrMakeProduct):number-of-units',
                            'Number of units'
                        )"
                    >
                        <b-form-input
                            v-model.number="numberOfUnits"
                            min="1"
                            step="1"
                            type="number"
                            :state="numberOfUnitsState"
                        />
                    </b-form-group>
                </b-col>
            </b-row>

            <div v-if="showSupplierDetails" class="product-component-modal__supplier-details">
                <b-row>
                    <b-col md="6">
                        <b-form-group
                            :label="translate(
                                'Component(Label):suppliers-product-name',
                                'Supplier’s product name'
                            )"
                        >
                            <b-form-input
                                v-model="supplierProductName"
                                :state="supplierProductNameState"
                            />
                        </b-form-group>
                    </b-col>
                    <b-col md="6">
                        <b-form-group
                            :label="translate(
                                'Component(Label):suppliers-product-id',
                                'Supplier’s product ID'
                            )"
                        >
                            <b-form-input v-model="supplierProductIdentifier" />
                        </b-form-group>
                    </b-col>
                </b-row>

                <SupplierSelector
                    :selected="suppliers"
                    @change="changeSuppliers"
                />
            </div>

            <div class="d-flex justify-content-end mt-2">
                <b-button
                    variant="outline-secondary"
                    :disabled="submitting"
                    @click="hide"
                >
                    {{ translate('Component(Button):cancel', 'Cancel') }}
                </b-button>
                <b-button
                    class="ml-1"
                    type="submit"
                    variant="primary"
                    :disabled="!isValid || submitting"
                >
                    <b-spinner v-if="submitting" small class="mr-50" />
                    {{ submitLabel }}
                </b-button>
            </div>
        </b-form>
    </b-modal>
</template>

<script>
import {
    BAlert,
    BButton,
    BCol,
    BForm,
    BFormGroup,
    BFormInput,
    BFormRadio,
    BFormRadioGroup,
    BModal,
    BRow,
    BSpinner,
} from 'bootstrap-vue'
import { VueAutosuggest } from 'vue-autosuggest'
import { SupplierSelector } from '@core/components/supply-chain'
import { ProductType } from '@/libs/product'

const SEARCH_DELAY = 600

export default {
    name: 'ProductComponentModal',
    components: {
        BAlert,
        BButton,
        BCol,
        BForm,
        BFormGroup,
        BFormInput,
        BFormRadio,
        BFormRadioGroup,
        BModal,
        BRow,
        BSpinner,
        SupplierSelector,
        VueAutosuggest,
    },
    props: {
        searchProducts: {
            type: Function,
            required: true,
        },
    },
    data() {
        return {
            mode: 'add',
            parent: null,
            component: null,
            nodeKey: null,
            name: '',
            selectedProduct: null,
            suggestions: [],
            productType: ProductType.OWN,
            numberOfUnits: 1,
            supplierProductName: '',
            supplierProductIdentifier: '',
            suppliers: [],
            submitting: false,
            errorMessage: '',
            searchTimeout: null,
            searchRevision: 0,
            productTypes: {
                own: ProductType.OWN,
                purchased: ProductType.PURCHASED,
            },
        }
    },
    computed: {
        inputProps() {
            return {
                id: 'product-structure-component-name',
                class: 'form-control w-100',
                name: 'product-structure-component-name',
                placeholder: this.translate(
                    'Page(ReceivedRequest):Component(ProductStructure):search-placeholder',
                    'Search or enter a new product name',
                ),
            }
        },
        isAddMode() {
            return this.mode === 'add'
        },
        isNewProduct() {
            return this.isAddMode && !this.selectedProduct
        },
        showSupplierDetails() {
            return (
                this.isNewProduct && this.productType === ProductType.PURCHASED
            ) || (
                !this.isAddMode
                && [ProductType.PURCHASED, ProductType.PURCHASED_UNDEFINED_SUPPLIER]
                    .includes(this.productType)
            )
        },
        numberOfUnitsState() {
            return Number.isInteger(Number(this.numberOfUnits)) && Number(this.numberOfUnits) > 0
                ? null
                : false
        },
        supplierProductNameState() {
            if (!this.showSupplierDetails) {
                return null
            }

            return this.supplierProductName.trim().length > 0 ? null : false
        },
        isValid() {
            return this.name.trim().length > 0
                && this.numberOfUnitsState !== false
                && this.supplierProductNameState !== false
        },
        modalTitle() {
            if (!this.isAddMode) {
                return this.translate(
                    'Page(ReceivedRequest):Component(ProductStructure):edit-component',
                    'Edit component',
                )
            }

            return this.translate(
                'Page(ReceivedRequest):Component(ProductStructure):add-component',
                'Add component',
            )
        },
        submitLabel() {
            return this.isAddMode
                ? this.translate(
                    'Page(ReceivedRequest):Component(ProductStructure):add-component',
                    'Add component',
                )
                : this.translate('Component(Button):save', 'Save')
        },
    },
    beforeDestroy() {
        clearTimeout(this.searchTimeout)
    },
    methods: {
        translate(key, fallback) {
            const translation = this.$t(key)
            return translation && translation !== key ? translation : fallback
        },
        reset() {
            clearTimeout(this.searchTimeout)
            this.mode = 'add'
            this.parent = null
            this.component = null
            this.nodeKey = null
            this.name = ''
            this.selectedProduct = null
            this.suggestions = []
            this.productType = ProductType.OWN
            this.numberOfUnits = 1
            this.supplierProductName = ''
            this.supplierProductIdentifier = ''
            this.suppliers = []
            this.submitting = false
            this.errorMessage = ''
            this.searchRevision += 1
        },
        showAdd(parent, nodeKey) {
            this.reset()
            this.parent = parent
            this.nodeKey = nodeKey
            this.$refs.modal.show()
        },
        showEdit(component, parent, nodeKey, details) {
            this.reset()
            this.mode = 'edit'
            this.component = component
            this.parent = parent
            this.nodeKey = nodeKey
            this.name = details.name || component.title || ''
            this.productType = details.type || component.type || ProductType.OWN
            this.numberOfUnits = Number(component.numberOfUnits) || 1
            this.supplierProductName = details.supplierProductName || ''
            this.supplierProductIdentifier = details.supplierProductIdentifier || ''
            this.suppliers = [...(details.suppliers || [])]
            this.$refs.modal.show()
        },
        hide() {
            if (!this.submitting) {
                this.$refs.modal.hide()
            }
        },
        handleHidden() {
            if (!this.submitting) {
                this.reset()
            }
        },
        getSuggestionValue(suggestion) {
            return suggestion.item.title
        },
        selectSuggestion(suggestion) {
            this.selectedProduct = suggestion.item
            this.name = suggestion.item.title
            this.suggestions = []
        },
        search(query) {
            this.name = query || ''

            if (this.selectedProduct && this.selectedProduct.title !== this.name) {
                this.selectedProduct = null
            }

            clearTimeout(this.searchTimeout)
            const revision = ++this.searchRevision

            if (this.name.trim().length < 2) {
                this.suggestions = []
                return
            }

            this.searchTimeout = setTimeout(() => {
                Promise.resolve(this.searchProducts(this.name.trim()))
                    .then(products => {
                        if (revision === this.searchRevision) {
                            this.suggestions = [{ data: products }]
                        }
                    })
                    .catch(() => {
                        if (revision === this.searchRevision) {
                            this.suggestions = []
                        }
                    })
            }, SEARCH_DELAY)
        },
        changeSuppliers(ids, suppliers) {
            this.suppliers = suppliers || []
        },
        submit() {
            if (!this.isValid || this.submitting) {
                return
            }

            this.submitting = true
            this.errorMessage = ''
            this.$emit('submit', {
                operation: this.mode,
                nodeKey: this.nodeKey,
                parentId: this.parent && this.parent.id,
                componentId: this.component && this.component.id,
                existingProductId: this.selectedProduct && this.selectedProduct.id,
                name: this.name.trim(),
                productType: this.productType,
                numberOfUnits: Number(this.numberOfUnits),
                supplierProductName: this.supplierProductName.trim(),
                supplierProductIdentifier: this.supplierProductIdentifier.trim(),
                suppliers: [...this.suppliers],
                successCallback: () => {
                    this.submitting = false
                    this.$refs.modal.hide()
                },
                failureCallback: error => {
                    this.submitting = false
                    this.errorMessage = error && error.message
                        ? error.message
                        : String(error || 'Unable to save the component.')
                },
            })
        },
    },
}
</script>

<style lang="scss" scoped>
.product-component-modal__supplier-details {
    padding-top: 1rem;
    border-top: 1px solid #ebe9f1;
}
</style>
